import { getFunctions, httpsCallable } from 'firebase/functions';
import { Analytics } from './analytics';
import { isLocal } from './constants';

/**
 * Log an error to Firebase (Cloud Functions + Analytics)
 */
export async function logError(
    error: Error | unknown,
    context: {
        page?: string;
        component?: string;
        action?: string;
        userId?: string;
        additionalInfo?: Record<string, any>;
    } = {}
) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    const errorName = error instanceof Error ? error.name : 'UnknownError';
    
    const errorData = {
        message: errorMessage,
        stack: errorStack,
        name: errorName,
        page: context.page || window.location.pathname,
        component: context.component,
        action: context.action,
        userId: context.userId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...context.additionalInfo
    };
    
    // Log to console in development
    if (isLocal) {
        console.error('🔴 Error logged:', errorData);
    }
    
    // Track in Analytics
    Analytics.error(
        errorName,
        errorMessage,
        errorData.page,
        errorStack
    );
    
    // Log to Firebase Cloud Functions (for server-side logging)
    try {
        const functions = getFunctions();
        const logErrorFn = httpsCallable(functions, 'logClientError');
        await logErrorFn(errorData);
    } catch (loggingError) {
        // Don't throw if logging fails - just console.error
        console.error('Failed to log error to Firebase:', loggingError);
    }
}

/**
 * Log a warning (non-critical issue)
 */
export function logWarning(
    message: string,
    context: {
        page?: string;
        component?: string;
        additionalInfo?: Record<string, any>;
    } = {}
) {
    const warningData = {
        message,
        page: context.page || window.location.pathname,
        component: context.component,
        timestamp: new Date().toISOString(),
        ...context.additionalInfo
    };
    
    if (isLocal) {
        console.warn('⚠️ Warning logged:', warningData);
    }
    
    // Track as custom event
    Analytics.error('warning', message, warningData.page);
}

/**
 * Log info (for debugging/tracking)
 */
export function logInfo(
    message: string,
    context: {
        page?: string;
        component?: string;
        additionalInfo?: Record<string, any>;
    } = {}
) {
    if (isLocal) {
        console.log('ℹ️ Info:', message, context);
    }
}

